// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Two diffs over token ids.
 *
 * The first is the prompt-cache view: given the token ids of two requests, find
 * the exact token where they stop matching. Everything before it can be reused
 * by a provider's prefix cache; everything after it is paid for again. One
 * changed character early in a prompt throws away the whole tail, which is why
 * a date injected by a chat template is expensive in a way nobody can see.
 *
 * The second is the model-vs-model view, which is an ordinary array diff.
 *
 * A note on what this can and cannot claim. The divergence point is a fact —
 * it is computed, not guessed. The *cause* named next to it is a diagnosis
 * drawn from the providers' documented invalidation rules and the shape of the
 * divergent tokens, so it is labelled as a likely cause, never as a certainty.
 * And cache expiry is invisible from here: two byte-identical requests can
 * still miss because the entry timed out. That is presented as something to
 * rule out, never as something this diff detected.
 */

import type { EncodeResult } from './tokenize';

export interface Divergence {
  /** Length of the shared prefix, in tokens. */
  lcp: number;
  /** Tokens of A after the divergence — lost from the cache. */
  aTail: number;
  /** Tokens of B after the divergence — must be processed fresh. */
  bTail: number;
  /** True when A is a pure prefix of B: perfectly reusable. */
  cleanPrefix: boolean;
  /** Share of A that a prefix cache could reuse. */
  reusable: number;
}

export function diverge(a: readonly number[], b: readonly number[]): Divergence {
  const n = Math.min(a.length, b.length);
  let i = 0;
  while (i < n && a[i] === b[i]) i += 1;
  return {
    lcp: i,
    aTail: a.length - i,
    bTail: b.length - i,
    cleanPrefix: i === a.length,
    reusable: a.length === 0 ? 0 : i / a.length,
  };
}

export type BreakerId =
  | 'volatile-date'
  | 'tool-definitions-changed'
  | 'serialisation-unstable'
  | 'system-prompt-edited'
  | 'below-minimum'
  | 'no-divergence';

export interface CacheBreaker {
  id: BreakerId;
  title: string;
  detail: string;
  /** What the user should do about it. */
  fix: string;
  confidence: 'observed' | 'likely';
}

/**
 * Documented minimum cacheable prefix lengths. Below these a provider will not
 * cache at all, so a prompt can be perfectly stable and still never hit.
 * Sourced from the providers' own caching documentation; shown in the UI with
 * that provenance so a stale number is visibly a number rather than a fact
 * chatprint invented.
 */
export const CACHE_MINIMUMS: Array<{ provider: string; tokens: number; note: string }> = [
  { provider: 'Anthropic (smaller models)', tokens: 2048, note: 'Minimum cacheable prefix; larger models cache from 1024.' },
  { provider: 'Anthropic (large models)', tokens: 1024, note: 'Minimum cacheable prefix.' },
  { provider: 'OpenAI', tokens: 1024, note: 'Caching applies from 1024 tokens and in increments above it.' },
  { provider: 'Google (implicit)', tokens: 2048, note: 'Implicit caching has a per-model floor in the low thousands.' },
];

const DATEISH = /\b(20\d\d|jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|\d{1,2}:\d{2}|\d{10,}|[0-9a-f]{8}-[0-9a-f]{4})\b/i;

export interface DiagnoseInput {
  a: EncodeResult;
  b: EncodeResult;
  divergence: Divergence;
  /** Token index where the tool definitions begin and end in A, if known. */
  toolRange?: { start: number; end: number } | null;
  /** Token index where the system prompt ends in A, if known. */
  systemEnd?: number | null;
}

/**
 * Name the most likely cause of the divergence.
 *
 * Deliberately returns at most one diagnosis plus any structural warnings: the
 * providers build their cache prefix in order (tools, then system, then
 * messages) and invalidate everything downstream of a change, so the *earliest*
 * divergence is the only one that matters. Listing every difference would be
 * noise dressed up as thoroughness.
 */
export function diagnose(input: DiagnoseInput): CacheBreaker[] {
  const { a, b, divergence, toolRange, systemEnd } = input;
  const out: CacheBreaker[] = [];

  if (divergence.aTail === 0 && divergence.bTail === 0) {
    out.push({
      id: 'no-divergence',
      title: 'Identical prompts',
      detail:
        'These two prompts are token-for-token identical, so a prefix cache can reuse all of it. If you are still seeing a miss, the entry has probably expired — providers keep a cached prefix alive for minutes, not hours.',
      fix: 'Rule out time-to-live expiry: a cache miss between two identical prompts is almost always the entry ageing out.',
      confidence: 'observed',
    });
    return out;
  }

  if (divergence.cleanPrefix) {
    out.push({
      id: 'no-divergence',
      title: 'Append-only — fully reusable',
      detail: `The first prompt is an exact prefix of the second. All ${divergence.lcp.toLocaleString()} of its tokens can be reused and only the ${divergence.bTail.toLocaleString()} new tokens need processing. This is the shape you want every turn to have.`,
      fix: 'Nothing to fix. Keep appending rather than rewriting earlier turns.',
      confidence: 'observed',
    });
    return out;
  }

  // Decode a small window either side of the divergence and look at it. The
  // byte-level and SentencePiece space markers are stripped so the quoted text
  // reads as the words it represents rather than as tokenizer internals.
  const readable = (t: string[]) => t.join('').replace(/Ġ/g, ' ').replace(/▁/g, ' ').replace(/Ċ/g, '\n').trim();
  const window = readable(a.tokens.slice(Math.max(0, divergence.lcp - 2), divergence.lcp + 4));
  const windowB = readable(b.tokens.slice(Math.max(0, divergence.lcp - 2), divergence.lcp + 4));

  const inTools = toolRange && divergence.lcp >= toolRange.start && divergence.lcp < toolRange.end;
  const inSystem = systemEnd != null && divergence.lcp < systemEnd;

  if (DATEISH.test(window) || DATEISH.test(windowB)) {
    out.push({
      id: 'volatile-date',
      title: 'A date, time or id changes near the start of the prompt',
      detail: `The prompts stop matching at token ${divergence.lcp.toLocaleString()}, and the tokens there look like a date, timestamp or identifier: ${JSON.stringify(window.slice(0, 60))} versus ${JSON.stringify(windowB.slice(0, 60))}. Everything after that point — ${divergence.aTail.toLocaleString()} tokens — is a fresh charge on every single call.`,
      fix: 'Move the volatile value out of the cached prefix: put it in the last user message rather than the system prompt. Note that some chat templates inject the date themselves, in which case the template, not your code, is breaking the cache.',
      confidence: 'likely',
    });
  } else if (inTools) {
    out.push({
      id: 'tool-definitions-changed',
      title: 'The tool definitions differ',
      detail: `Divergence lands inside the tool definitions, at token ${divergence.lcp.toLocaleString()}. Providers build the cached prefix as tools, then system prompt, then messages, and a change at one level invalidates that level and everything after it — so a reordered or edited tool throws away the entire prompt.`,
      fix: 'Keep the tool array in a fixed order and avoid regenerating descriptions per request. If a tool is only sometimes needed, consider whether adding it conditionally is worth losing the whole cache.',
      confidence: 'likely',
    });
  } else if (Math.abs(divergence.aTail - divergence.bTail) <= 2 && divergence.aTail > 0) {
    out.push({
      id: 'serialisation-unstable',
      title: 'Same content, different serialisation',
      detail: `The two prompts diverge at token ${divergence.lcp.toLocaleString()} but the remainders are almost exactly the same length (${divergence.aTail.toLocaleString()} versus ${divergence.bTail.toLocaleString()}). That is the signature of identical data serialised differently — JSON object key order is the usual culprit.`,
      fix: 'Serialise tool schemas and any injected JSON with a stable key order so the bytes are reproducible between calls.',
      confidence: 'likely',
    });
  } else if (inSystem) {
    out.push({
      id: 'system-prompt-edited',
      title: 'The system prompt changed',
      detail: `Divergence is at token ${divergence.lcp.toLocaleString()}, inside the system prompt, which costs you the whole ${divergence.aTail.toLocaleString()}-token tail.`,
      fix: 'Keep the system prompt byte-stable across a session. Per-user or per-request text belongs later in the conversation, after the part you want cached.',
      confidence: 'likely',
    });
  } else {
    out.push({
      id: 'system-prompt-edited',
      title: 'The prompts diverge mid-conversation',
      detail: `The shared prefix ends at token ${divergence.lcp.toLocaleString()} — ${(divergence.reusable * 100).toFixed(1)}% of the first prompt — after which ${divergence.aTail.toLocaleString()} tokens are no longer reusable.`,
      fix: 'Check whether an earlier turn was rewritten, summarised or re-ordered between the two calls. Editing history invalidates everything downstream of the edit.',
      confidence: 'likely',
    });
  }

  const shortest = Math.min(a.ids.length, b.ids.length);
  const failing = CACHE_MINIMUMS.filter((m) => shortest < m.tokens);
  if (failing.length) {
    out.push({
      id: 'below-minimum',
      title: 'Too short to be cached at all',
      detail: `At ${shortest.toLocaleString()} tokens this prompt is below the documented minimum cacheable length for ${failing.map((f) => f.provider).join(', ')}. No amount of prefix stability will produce a hit.`,
      fix: 'Either accept that this prompt is not a caching candidate, or check the figures against your provider’s current documentation — these minimums change.',
      confidence: 'observed',
    });
  }

  return out;
}

export interface ModelComparison {
  label: string;
  total: number;
  /** Difference against the first model in the comparison. */
  delta: number;
  contextWindow: number | null;
  fits: boolean;
  estimate: boolean;
  note?: string;
}

/** Build the model-vs-model table rows, relative to the first entry. */
export function compareModels(
  rows: Array<{ label: string; total: number; contextWindow: number | null; estimate: boolean; note?: string }>,
): ModelComparison[] {
  if (rows.length === 0) return [];
  const base = rows[0].total;
  return rows.map((r) => ({
    label: r.label,
    total: r.total,
    delta: r.total - base,
    contextWindow: r.contextWindow,
    fits: r.contextWindow === null ? true : r.total <= r.contextWindow,
    estimate: r.estimate,
    note: r.note,
  }));
}
